import React, { useMemo, useState } from 'react';
import { useDisclosure, useResizeObserver } from '@mantine/hooks';
import { Popover, PopoverProps } from '../Popover/Popover';
import { Container } from './LongContentPopover.styles';
import { useIsVerticalOverflow } from '../hooks';
import { CopyButton, CopyButtonProps } from '../CopyButton/CopyButton';
import styled from 'styled-components';

export type LongContentPopoverProps = Omit<PopoverProps, 'body'> & {
  reverseEllipsis?: boolean; // New prop to control ellipsis direction
  popoverContent?: React.ReactNode;
  copyable?: boolean;
  copyButtonSize?: CopyButtonProps['size'];
  alwaysShowPopoverOnHover?: boolean;
};

export const StyledCopyButton = styled(CopyButton)`
  padding: 0px;
  position: relative;
  visibility: visible;
  left: 8px;
`;

const StyledCopyContainer = styled.div`
  position: relative;
  display: inline;
  direction: rtl;
`;

const CopyButtonContainer = styled.div`
  position: absolute;
  top: -2px;
  right: 7px;

  button {
    padding: 4px 8px;
  }
`;

export default function LongContentPopover({
  children,
  position = 'top',
  reverseEllipsis = false, // Default value is false
  popoverContent,
  copyable = false,
  copyButtonSize,
  alwaysShowPopoverOnHover = false,
  ...rest
}: LongContentPopoverProps) {
  const [opened, { open, close }] = useDisclosure();
  const [ref] = useResizeObserver();
  const isOverflown = useIsVerticalOverflow(ref);
  const [isHovered, setIsHovered] = useState(false);

  const normalizedChildren = useMemo(() => {
    if (typeof children !== 'string') {
      return children;
    }

    // if reverseEllipsis is true, remove '/' chars, '../', and './' from the beginning of the string
    if (reverseEllipsis) {
      return children.replace(/^(\/+|\.\.\/|\.\/)+/g, '');
    }

    return children;
  }, [children]);

  return (
    <Popover
      body={popoverContent ?? children}
      opened={opened}
      position={position}
      {...rest}
      clickable={false}
    >
      <Container
        onMouseEnter={() => {
          setIsHovered(true);
          if (isOverflown || alwaysShowPopoverOnHover) {
            open();
          }
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          close();
        }}
        ref={ref}
        $reverseEllipsis={isOverflown && reverseEllipsis} // Pass the prop to the styled component
      >
        {copyable ? (
          <StyledCopyContainer>
            {isHovered && (
              <CopyButtonContainer>
                <StyledCopyButton
                  size={copyButtonSize}
                  value={normalizedChildren as string}
                />
              </CopyButtonContainer>
            )}
            {normalizedChildren}
          </StyledCopyContainer>
        ) : (
          normalizedChildren
        )}
      </Container>
    </Popover>
  );
}
